/**
 * Accessible megamenu
 * Based on legacy Adobe Accessible menu
 * https://adobe-accessibility.github.io/Accessible-Mega-Menu/
 */

interface MegaMenuSettings {
    uuidPrefix: string;
    menuClass: string;
    topNavItemClass: string;
    panelClass: string;
    panelGroupClass: string;
    hoverClass: string;
    focusClass: string;
    openClass: string;
    toggleButtonClass: string;
}

export class AccessibleMegaMenu {
    private element: HTMLElement;
    private settings: MegaMenuSettings;
    private menu: HTMLUListElement | HTMLOListElement | null = null;
    private topnavitems: HTMLElement[] = [];
    private panels: NodeListOf<HTMLElement> | null = null;
    private toggleButton: HTMLButtonElement | null = null;
    private mouseTimeoutID: number | null = null;
    private focusTimeoutID: number | null = null;
    private uuid: number = 0;

    constructor(element: HTMLElement, options?: Partial<MegaMenuSettings>) {
        this.element = element;

        const defaults: MegaMenuSettings = {
            uuidPrefix: "accessible-megamenu",
            menuClass: "accessible-megamenu",
            topNavItemClass: "accessible-megamenu-top-nav-item",
            panelClass: "accessible-megamenu-panel",
            panelGroupClass: "accessible-megamenu-panel-group",
            hoverClass: "hover",
            focusClass: "focus",
            openClass: "open",
            toggleButtonClass: "accessible-megamenu-toggle",
        };

        this.settings = { ...defaults, ...options };
        this.init();
    }

    private _isTabbable(el: HTMLElement): boolean {
        const tagName = el.tagName.toLowerCase();
        const hasTabIndex =
            el.hasAttribute("tabindex") && el.getAttribute("tabindex") !== "-1";
        const isClickable =
            (tagName === "a" && el.hasAttribute("href")) ||
            (["button", "input", "select", "textarea"].includes(tagName) &&
                !(el as any).disabled);

        return (isClickable || hasTabIndex) && el.offsetParent !== null;
    }

    private _getTabbables(container: HTMLElement): HTMLElement[] {
        const elements = Array.from(
            container.querySelectorAll<HTMLElement>(
                "a, button, input, select, textarea, [tabindex]"
            )
        );
        return elements.filter((el) => this._isTabbable(el));
    }

    private _addUniqueId(el: HTMLElement): void {
        if (!el.id) {
            el.id = `${this.settings.uuidPrefix}-${Date.now()}-${++this.uuid}`;
        }
    }

    private init(): void {
        const s = this.settings;
        this.menu = this.element.querySelector<
            HTMLUListElement | HTMLOListElement
        >("ol, ul");

        if (!this.menu) return;

        this.topnavitems = Array.from(this.menu.children) as HTMLElement[];
        this.toggleButton =
            this.element.querySelector<HTMLButtonElement>("button");

        this.element.setAttribute("role", "navigation");
        this._addUniqueId(this.menu);
        this.menu.classList.add(s.menuClass, `js-${s.menuClass}`);

        this.topnavitems.forEach((item) => {
            item.classList.add(s.topNavItemClass);

            const link = item.querySelector<HTMLElement>("a, button");

            const panel = link?.nextElementSibling as HTMLElement;

            if (link) this._addUniqueId(link);

            if (link && panel && !link.contains(panel)) {
                this._addUniqueId(panel);

                link.setAttribute("role", "button");
                link.setAttribute("aria-controls", panel.id);
                link.setAttribute("aria-expanded", "false");
                link.setAttribute("tabindex", "0");

                panel.setAttribute("role", "region");
                panel.setAttribute("aria-hidden", "true");
                panel.classList.add(s.panelClass);

                if (!panel.hasAttribute("aria-labelledby")) {
                    panel.setAttribute("aria-labelledby", link.id);
                }
            }
        });

        this.panels = this.menu.querySelectorAll<HTMLElement>(
            `.${s.panelClass}`
        );
        this.menu
            .querySelectorAll("hr")
            .forEach((hr) => hr.setAttribute("role", "separator"));

        if (this.toggleButton) {
            this.toggleButton.classList.add(s.toggleButtonClass);
            this.toggleButton.setAttribute("aria-expanded", "false");
            this.toggleButton.setAttribute("aria-controls", this.menu.id);
        }

        this._bindEvents();
    }

    private _bindEvents(): void {
        if (!this.menu) return;

        this.menu.addEventListener("focusin", (e) =>
            this._handleFocusIn(e as FocusEvent)
        );
        this.menu.addEventListener("focusout", (e) =>
            this._handleFocusOut(e as FocusEvent)
        );
        this.menu.addEventListener("keydown", (e) =>
            this._handleKeyDown(e as KeyboardEvent)
        );
        this.menu.addEventListener("mousedown", () => this._handleMouseDown());
        this.menu.addEventListener("click", (e) =>
            this._handleClick(e as MouseEvent)
        );

        if (this.toggleButton) {
            this.toggleButton.addEventListener("click", () => {
                const expanded =
                    this.toggleButton?.getAttribute("aria-expanded") === "true";
                this.toggleButton?.setAttribute(
                    "aria-expanded",
                    String(!expanded)
                );
            });
        }

        document.addEventListener("mouseup", (e: MouseEvent) => {
            if (!this.element.contains(e.target as Node)) {
                this._togglePanel(e, true);
            }
        });
    }

    private _togglePanel(event: Event, hide: boolean = false): void {
        const s = this.settings;

        if (hide) {
            this.topnavitems.forEach((item) => {
                const btn = item.querySelector("[aria-expanded]");
                const panel = item.querySelector(`.${s.panelClass}`);
                btn?.setAttribute("aria-expanded", "false");
                btn?.classList.remove(s.openClass);
                panel?.classList.remove(s.openClass);
                panel?.setAttribute("aria-hidden", "true");
            });
            return;
        }

        const target = (event.target as HTMLElement).closest<HTMLElement>(
            "a, button, [tabindex]"
        );
        const topli = target?.closest(`.${s.topNavItemClass}`);

        if (topli) {
            // Close all others first
            this.topnavitems.forEach((item) => {
                if (item !== topli) {
                    const otherBtn = item.querySelector("[aria-expanded]");
                    const otherPanel = item.querySelector(`.${s.panelClass}`);
                    otherBtn?.setAttribute("aria-expanded", "false");
                    otherBtn?.classList.remove(s.openClass);
                    otherPanel?.classList.remove(s.openClass);
                    otherPanel?.setAttribute("aria-hidden", "true");
                }
            });

            // Toggle current
            const btn = topli.querySelector("[aria-expanded]");
            const panel = topli.querySelector(`.${s.panelClass}`);

            // In togglePanel, we assume we are opening because handleClick handles the "already open" check
            btn?.setAttribute("aria-expanded", "true");
            btn?.classList.add(s.openClass);
            panel?.classList.add(s.openClass);
            panel?.setAttribute("aria-hidden", "false");
        }
    }

    private _handleClick(e: MouseEvent): void {
        const target = (e.target as HTMLElement).closest<HTMLElement>(
            "a, button, [tabindex]"
        );
        if (!target) return;

        const topli = target.closest(`.${this.settings.topNavItemClass}`);
        const panel = target.closest(`.${this.settings.panelClass}`);

        if (topli && !panel) {
            const hasPanel = topli.querySelector(
                `.${this.settings.panelClass}`
            );
            if (hasPanel) {
                e.preventDefault();
                e.stopPropagation();

                const isExpanded =
                    target.getAttribute("aria-expanded") === "true";

                if (isExpanded) {
                    this._togglePanel(e, true);
                } else {
                    this._togglePanel(e);
                }
            }
        }
    }

    private _handleFocusIn(e: FocusEvent): void {
        if (this.focusTimeoutID) window.clearTimeout(this.focusTimeoutID);
        (e.target as HTMLElement).classList.add(this.settings.focusClass);
    }

    private _handleFocusOut(e: FocusEvent): void {
        (e.target as HTMLElement).classList.remove(this.settings.focusClass);
        
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        
        // If focus is moving to another element within the menu, don't close
        if (relatedTarget && this.element.contains(relatedTarget)) {
            return;
        }

        if (relatedTarget && !this.element.contains(relatedTarget)) {
            this._togglePanel(e, true);
            return;
        }
    
    }

    private _handleKeyDown(e: KeyboardEvent): void {
        const target = e.target as HTMLElement;
        const topli = target.closest<HTMLElement>(
            `.${this.settings.topNavItemClass}`
        );
        if (!this.menu) return;

        const allTabbables = this._getTabbables(this.menu);
        const index = allTabbables.indexOf(target);

        switch (e.key) {
            case "Escape":
                e.preventDefault();
                // Close all panels
                this._togglePanel(e, true);
                // Return focus to the top-level link if focus was inside a panel
                const parentLink =
                    topli?.querySelector<HTMLElement>("a, button");
                if (parentLink) {
                    parentLink.focus();
                }
                break;
            case "ArrowDown":
                e.preventDefault();
                if (index < allTabbables.length - 1)
                    allTabbables[index + 1].focus();
                break;
            case "ArrowUp":
                e.preventDefault();
                if (index > 0) allTabbables[index - 1].focus();
                break;
            case "ArrowRight":
                e.preventDefault();
                (topli?.nextElementSibling as HTMLElement)
                    ?.querySelector<HTMLElement>("a, button")
                    ?.focus();
                break;
            case "ArrowLeft":
                e.preventDefault();
                (topli?.previousElementSibling as HTMLElement)
                    ?.querySelector<HTMLElement>("a, button")
                    ?.focus();
                break;
        }
    }

    private _handleMouseDown(): void {
        if (this.mouseTimeoutID) window.clearTimeout(this.mouseTimeoutID);
    }
}
